/**
 * The provider catalogue, as the UI needs it.
 *
 * `GET /integrations/providers` returns what each provider *is* — auth kind,
 * capabilities, which credentials it needs. It does not return a human name, a
 * category, or a sentence explaining why a campaign would turn it on, because
 * those are presentation concerns and the backend is right not to hold them.
 * They live here.
 *
 * ## Why this file sorts
 *
 * `ProviderCatalogue.CATALOGUE` is a `Map.ofEntries(...)`, and `values()` on an
 * immutable map has a **deliberately randomised iteration order** — the JDK
 * salts it per JVM so nobody depends on it. So `/integrations/providers` comes
 * back in a different order after every backend restart. A sidebar that
 * reshuffles itself overnight is worse than a wrong one: people navigate by
 * muscle memory, by position, and a list that moves silently retrains them into
 * clicking the wrong thing. `ORDER` below fixes it, in the app, where ordering
 * belongs.
 *
 * A provider the backend adds that is missing from these maps still renders —
 * humanised from its code, uncategorised, blurb-less. Unknown must degrade, not
 * disappear: a provider that exists but is invisible in the sidebar is how an
 * integration ends up configured by nobody because nobody knew it was there.
 */

/** Display names. Kenyan services keep the name people actually say. */
export const PROVIDER_LABEL: Record<string, string> = {
  ECITIZEN_IPRS: 'eCitizen IPRS',
  SAFARICOM_DARAJA: 'M-Pesa Daraja',
  AFRICASTALKING: "Africa's Talking",
  ZEPTOMAIL: 'ZeptoMail',
  MAPBOX: 'Mapbox',
  X: 'X (Twitter) API',
  META: 'Meta Graph API',
  TIKTOK: 'TikTok Research API',
  YOUTUBE: 'YouTube Data API',
  TIKHUB: 'TikHub aggregator',
  KENYA_LAW: 'Kenya Law',
  KENYA_GAZETTE: 'Kenya Gazette',
  BRS: 'Business Registration Service',
  GENERIC_RSS: 'RSS feeds',
  GENERIC_HTTP: 'HTTP fetch',
}

/**
 * One line on why a campaign would enable it. Shown on the provider page before
 * anything is configured — the moment when "should we turn this on?" is the only
 * question being asked.
 */
export const PROVIDER_BLURB: Record<string, string> = {
  ECITIZEN_IPRS:
    'Checks a national ID against the national identity register, so a voter record can be started from the state’s own data instead of being typed from a card.',
  SAFARICOM_DARAJA:
    'M-Pesa collections and payouts — contributions in, per-diems and airtime out, each with its own callback.',
  AFRICASTALKING:
    'Bulk SMS, USSD and airtime. The fallback channel when a voter has a phone but no smartphone.',
  ZEPTOMAIL:
    'Transactional email — invitations, verification codes, export links. Not a bulk campaign sender.',
  MAPBOX:
    'Map tiles and geocoding for the geography screens and for placing a residence on a map.',
  X: 'Reads posts and search results for sentiment and issue tracking. Read-only; nothing is ever posted.',
  META: 'Facebook and Instagram — page and hashtag reads, and the comment writes the war room replies with. One credential fronts both.',
  TIKTOK: 'Public video search and comments, for watching what is spreading. TikTok sells no way to reply, so this is read-only.',
  YOUTUBE: 'Video search, comment reads and comment writes — the one social platform here that supports the full watch-and-reply loop.',
  TIKHUB:
    'One key that reads X, TikTok, Instagram, Reddit and more. Not a platform in its own right — a ROUTE to them, so a thread it finds still belongs to the platform it was posted on.',
  KENYA_LAW: 'Case-law search, for compliance and petition research.',
  KENYA_GAZETTE: 'Gazette notices — appointments, boundaries, election notices.',
  BRS: 'Company registry lookups, for vetting a supplier or a donor.',
  GENERIC_RSS: 'Any RSS feed, for news monitoring without a bespoke adapter.',
  GENERIC_HTTP: 'A host-allowlisted fetcher for one-off sources. The escape hatch, used sparingly.',
}

/**
 * A provider the registry lists but does not actually drive.
 *
 * ZeptoMail is the one case today. It is in the backend's provider catalogue with an
 * `email.send` capability and an `API_KEY` credential, so this UI will happily let you
 * register it — and registering it **changes nothing about whether email is delivered**.
 * Transactional mail goes through `shared.notification.ZeptoMailClient`, which reads
 * `axis.mail.zepto.*` from the backend environment and never consults the registry.
 *
 * That is a genuine gap in the backend rather than a UI decision, and it is exactly the
 * kind of gap that costs someone an afternoon: they register the provider, wait for an
 * invitation email, see no calls in the integration call log, and conclude the provider
 * is broken. So the page says it, in the place where the wrong conclusion gets drawn.
 *
 * Remove the entry here the day the mail client resolves its key from the registry.
 */
export const PROVIDER_NOT_WIRED: Record<string, { where: string; evidence: string }> = {
  ZEPTOMAIL: {
    where: 'the backend environment — AXIS_ZEPTO_MAIL_API_KEY, AXIS_ZEPTO_MAIL_FROM and'
      + ' AXIS_ZEPTO_MAIL_FROM_NAME in the API\'s .env, read into axis.mail.zepto.*',
    evidence: 'Notification Log',
  },
}

/**
 * The registry's own category vocabulary, pre-filled when registering so nobody
 * has to guess which of twelve enum values a provider belongs to.
 */
export const PROVIDER_CATEGORY: Record<string, string> = {
  ECITIZEN_IPRS: 'IDENTITY',
  SAFARICOM_DARAJA: 'PAYMENT',
  AFRICASTALKING: 'MESSAGING',
  ZEPTOMAIL: 'MESSAGING',
  MAPBOX: 'MAPPING',
  X: 'SOCIAL',
  META: 'SOCIAL',
  TIKTOK: 'SOCIAL',
  YOUTUBE: 'SOCIAL',
  TIKHUB: 'SOCIAL',
  KENYA_LAW: 'LEGAL',
  KENYA_GAZETTE: 'LEGAL',
  BRS: 'REGISTRY',
  GENERIC_RSS: 'NEWS',
  GENERIC_HTTP: 'OTHER',
}

/**
 * Sidebar order. Grouped by what the thing is for, most-used first inside each
 * group, rather than alphabetically — "M-Pesa" and "Mapbox" sitting next to each
 * other because of a shared prefix helps nobody. Anything absent sorts to the
 * end, alphabetically, so a new backend provider appears predictably.
 */
const ORDER = [
  'ECITIZEN_IPRS',
  'SAFARICOM_DARAJA',
  'AFRICASTALKING',
  'ZEPTOMAIL',
  'X',
  'META',
  'TIKTOK',
  'YOUTUBE',
  'TIKHUB',
  'MAPBOX',
  'KENYA_LAW',
  'KENYA_GAZETTE',
  'BRS',
  'GENERIC_RSS',
  'GENERIC_HTTP',
]

/** `SAFARICOM_DARAJA` → `Safaricom Daraja`. Only reached for unknown codes. */
export function providerLabel(code: string): string {
  const known = PROVIDER_LABEL[code]
  if (known) return known
  return code
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export function providerRank(code: string): number {
  const i = ORDER.indexOf(code)
  return i === -1 ? ORDER.length : i
}

/** Stable across backend restarts, which the API's own order is not. */
export function sortProviderCodes(codes: string[]): string[] {
  return [...codes].sort((a, b) => {
    const d = providerRank(a) - providerRank(b)
    return d !== 0 ? d : providerLabel(a).localeCompare(providerLabel(b))
  })
}

/**
 * What to put in the boxes at registration.
 *
 * The catalogue tells you a provider declares `token_url`; it does not tell you
 * that eCitizen's is `oauth/access-token` and that leaving it blank makes every
 * lookup fail *silently* — the adapter catches the exchange error and returns
 * "not found", because an identity-service outage must never block registration.
 * That is the right behaviour and a miserable thing to debug, so the values that
 * are knowable are pre-filled and the rest are explained.
 *
 * These are defaults, not constants: every one stays editable, and a deployment
 * pointing at a sandbox host will need to change the base URL.
 */
export interface ProviderSetup {
  /** The host the adapter calls. Stored on the integration, not in config. */
  baseUrl?: string
  /** Pre-filled values for the provider's declared config keys. */
  config?: Record<string, string>
  /** One line per key, shown under its input. */
  hints?: Record<string, string>
}

export const PROVIDER_SETUP: Record<string, ProviderSetup> = {
  ECITIZEN_IPRS: {
    baseUrl: 'https://accounts.ecitizen.go.ke',
    config: {
      token_url: 'oauth/access-token',
      id_type: 'citizen',
      timeout_ms: '8000',
    },
    hints: {
      token_url:
        'Where the client ID and secret are exchanged for a token. Relative to the base URL, or an absolute https:// URL. Required — without it every lookup quietly returns "not found".',
      id_type: 'Sent with each query. `citizen` unless eCitizen has told you otherwise.',
      timeout_ms: 'How long to wait before giving up and letting the operator type the record in.',
      base_url: 'Leave empty. Only for the rare case where the token endpoint is on a different host from the lookup — the Base URL field above is the one that matters.',
    },
  },
  SAFARICOM_DARAJA: {
    baseUrl: 'https://sandbox.safaricom.co.ke',
    hints: { endpoint: 'Which Daraja endpoint set this integration targets.' },
  },
  ZEPTOMAIL: { baseUrl: 'https://api.zeptomail.com' },
  MAPBOX: { baseUrl: 'https://api.mapbox.com' },

  // The social hosts. Each driver builds a versioned path onto these, so the base URL is the
  // HOST only — except Meta, whose Graph version is part of the path prefix and therefore has
  // to live here. Getting one of these wrong produces a 404 that reads as "the platform is
  // down", which is why they are pre-filled rather than typed.
  X: {
    baseUrl: 'https://api.x.com',
    config: { token_url: 'https://api.x.com/2/oauth2/token' },
    hints: {
      token_url:
        'Where a refresh token is exchanged for a new access token. Leave it — with it set and a REFRESH_TOKEN stored, Axis renews the token before every expiry and can post unattended. Without it, an OAuth 2.0 user token dies after two hours.',
    },
  },
  META: {
    baseUrl: 'https://graph.facebook.com/v21.0',
    hints: {
      timeout_ms: 'Optional. Graph is usually fast; raise it only if you see transport errors.',
    },
  },
  YOUTUBE: {
    baseUrl: 'https://www.googleapis.com',
    config: { token_url: 'https://oauth2.googleapis.com/token' },
    hints: {
      token_url:
        'Google’s token endpoint. Required for unattended use — with a REFRESH_TOKEN stored, Axis renews the access token before it expires.',
      timeout_ms: 'Optional. The default 15s is comfortable for Data API v3.',
    },
  },
  TIKHUB: {
    baseUrl: 'https://api.tikhub.io',
    config: { max_reply_depth: '2', media_download: 'true' },
    hints: {
      max_reply_depth:
        'How far down a reply tree to follow. A comment thread is a tree and the aggregator charges per call, so “all of it” is not a default. 2 is usually the argument; 3 is where a pile-on lives.',
      media_download:
        'Pull images and video into our own storage rather than linking them. TikTok’s payload carries cdn_url_expired — their media URLs expire, so a linked thread loses its evidence.',
      search_path:
        'Leave blank. Only set this if the aggregator moves an endpoint and you need to correct it without waiting on a release.',
      comments_path: 'Leave blank unless an endpoint has moved.',
      post_path: 'Leave blank unless an endpoint has moved.',
      timeout_ms: 'Aggregated fetches are slower than a first-party call. 30000 is comfortable.',
    },
  },
  TIKTOK: {
    baseUrl: 'https://open.tiktokapis.com',
    config: { token_url: 'https://open.tiktokapis.com/v2/oauth/token/' },
    hints: {
      token_url:
        'TikTok’s token endpoint. Required for unattended use — TikTok access tokens are short-lived and rotate their refresh token on every renewal.',
      timeout_ms: 'Optional. Research API queries can be slow; 30000 is a reasonable ceiling.',
    },
  },
}
